// /src/pages/private/members/academyTools/components/AcademyResourceCards.jsx

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AcademyDocumentCard from './AcademyDocumentCard';
import AcademyDocumentListRow from './AcademyDocumentListRow';
import AcademyCourseLockPanel from './AcademyCourseLockPanel';
import AcademyExamRoomButton from './AcademyExamRoomButton';
import { filterAcademyDocuments } from '../utils/documentPreviewCache';

export default function AcademyResourceCards({
  course,
  searchTerm,
  viewMode,
  onOpenDocument,
  onOpenExamDisclaimer,
}) {
  const { t } = useTranslation();
  const documentsLocked = Boolean(course?.promotionGateLocked);

  const filteredDocuments = useMemo(() => {
    if (!course || documentsLocked) return [];
    return filterAcademyDocuments(course.documents || [], searchTerm);
  }, [course, searchTerm, documentsLocked]);
  if (!course) {
    return (
      <div className="member-academy-detail member-academy-detail--empty">
        <p className="member-academy-detail__empty">{t('academySelectCoursePrompt')}</p>
      </div>
    );
  }

  const hasDocuments = documentsLocked
    ? (course.documentCount ?? 0) > 0
    : (course.documents || []).length > 0;
  const hasSearch = Boolean(String(searchTerm || '').trim());
  const showExamRoom = Boolean(course?.examRoomOpen && (course?.examQuestionCount ?? 0) > 0);

  const detailContent = (
    <>
      <header className="member-academy-detail__header">
        <div className="member-academy-detail__header-top">
          <div className="member-academy-detail__header-main">
            <h3 className="member-academy-detail__title">{course.displayName}</h3>
            <div className="member-academy-detail__meta">
              {course.trainingLevelName ? (
                <span className="member-academy-detail__badge">{course.trainingLevelName}</span>
              ) : null}
              {course.isNextRankRequired ? (
                <span className="member-academy-detail__badge member-academy-detail__badge--next-rank">
                  Next rank
                </span>
              ) : null}
              {course.version ? (
                <span className="member-academy-detail__badge">v{course.version}</span>
              ) : null}
            </div>
          </div>

          {showExamRoom ? (
            <div className="member-academy-detail__header-actions">
              <AcademyExamRoomButton onClick={() => onOpenExamDisclaimer?.(course)} />
            </div>
          ) : null}
        </div>

        {course.description ? (
          <p className="member-academy-detail__description">{course.description}</p>
        ) : null}
      </header>

      <section className="member-academy-detail__section">
        <h4 className="member-academy-detail__section-title">Documents</h4>

        {!hasDocuments ? (
          <p className="member-academy-detail__section-empty">
            {t('academyNoDocuments')}
          </p>
        ) : documentsLocked ? (
          <p className="member-academy-detail__section-empty member-academy-detail__section-empty--locked">
            {course.documentCount ?? 0} document{(course.documentCount ?? 0) === 1 ? '' : 's'} pending unlock
          </p>
        ) : filteredDocuments.length === 0 ? (
          <p className="member-academy-detail__section-empty">
            {t('academyNoDocumentsMatchSearch')}
          </p>
        ) : viewMode === 'list' ? (
          <div className="member-academy-document-list">
            {filteredDocuments.map((document) => (
              <AcademyDocumentListRow
                key={document.documentName}
                document={document}
                onOpenDocument={onOpenDocument}
              />
            ))}
          </div>
        ) : (
          <div className="member-academy-document-grid">
            {filteredDocuments.map((document) => (
              <AcademyDocumentCard
                key={document.documentName}
                document={document}
                onOpenDocument={onOpenDocument}
              />
            ))}
          </div>
        )}

        {hasSearch && filteredDocuments.length > 0 ? (
          <p className="member-academy-detail__search-meta">
            {t('academyDocumentsMatchCount', { count: filteredDocuments.length })}
          </p>
        ) : null}
      </section>
    </>
  );

  return (
    <div
      className={[
        'member-academy-detail',
        documentsLocked ? 'member-academy-detail--promotion-locked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <AcademyCourseLockPanel course={course} viewMode={viewMode}>
        {detailContent}
      </AcademyCourseLockPanel>
    </div>
  );
}
